// src/services/employee.service.js
import { api } from './api';

class EmployeeService {
    /**
     * Get all employees for the current company
     */
    async getAllEmployees() {
        try {
            // Try the new employees endpoint first
            const response = await api.get('/api/employees');
            return response.data.data || response.data.employees || [];
        } catch (error) {
            console.error('Error fetching from /api/employees, trying /api/admin/employees:', error);
            // Fallback to admin endpoint
            try {
                const response = await api.get('/api/admin/employees');
                return response.data.employees || response.data.data || [];
            } catch (adminError) {
                console.error('Error fetching from both endpoints:', adminError);
                // Fallback to localStorage if both APIs fail
                return this.getLocalStorageEmployees();
            }
        }
    }

    /**
     * Get employee by ID
     */
    async getEmployeeById(id) {
        try {
            const response = await api.get(`/api/employees/${id}`);
            return response.data; // Return full response object with success flag
        } catch (error) {
            console.error('Error fetching employee:', error);
            throw error;
        }
    }

    /**
     * Get current user's profile
     */
    async getProfile() {
        try {
            const response = await api.get('/api/employees/profile');
            return response.data.employee;
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw error;
        }
    }

    /**
     * Update employee profile
     */
    async updateProfile(data) {
        try {
            const response = await api.put('/api/employees/profile', data);
            return response.data.employee;
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }

    /**
     * Get team members (for managers)
     */
    async getTeamMembers() {
        try {
            const response = await api.get('/api/employees/team');
            return response.data.teamMembers || [];
        } catch (error) {
            console.error('Error fetching team members:', error);
            return [];
        }
    }

    /**
     * Get next available employee code from backend
     */
    async getNextEmployeeCode() {
        try {
            const response = await api.get('/api/admin/employees/next-code');
            return response.data;
        } catch (error) {
            console.error('Error getting next employee code:', error);
            return null;
        }
    }

    /**
     * Add new employee
     */
    async addEmployee(employeeData) {
        try {
            const response = await api.post('/api/admin/employees', employeeData);
            return response.data.employee;
        } catch (error) {
            console.error('Error adding employee:', error);
            throw error;
        }
    }

    /**
     * Bulk import employees
     */
    async bulkImportEmployees(employeesData) {
        try {
            const response = await api.post('/api/admin/employees/bulk-import', {
                employees: employeesData
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk importing employees:', error);
            throw error;
        }
    }

    /**
     * Update employee
     */
    async updateEmployee(employeeId, updates) {
        try {
            // Map frontend field names to backend field names
            const mappedUpdates = { ...updates };
            if (updates.managerId !== undefined) {
                mappedUpdates.reporting_manager_id = updates.managerId;
                delete mappedUpdates.managerId;
            }
            if (updates.accessLevel !== undefined) {
                mappedUpdates.access_level = updates.accessLevel;
                delete mappedUpdates.accessLevel;
            }
            if (updates.secondaryAccess !== undefined) {
                mappedUpdates.secondary_access = updates.secondaryAccess;
                delete mappedUpdates.secondaryAccess;
            }

            const response = await api.put(`/api/admin/employees/${employeeId}`, mappedUpdates);
            return response.data.employee;
        } catch (error) {
            console.error('Error updating employee:', error);
            throw error;
        }
    }

    /**
     * Delete employee (soft delete)
     */
    async deleteEmployee(employeeId) {
        try {
            const response = await api.delete(`/api/admin/employees/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting employee:', error);
            throw error;
        }
    }

    /**
     * Get departments
     */
    async getDepartments() {
        try {
            const response = await api.get('/api/admin/departments');
            return response.data.departments || [];
        } catch (error) {
            console.error('Error fetching departments:', error);
            return [];
        }
    }

    /**
     * Update employee shift
     */
    async updateEmployeeShift(employeeId, shiftId) {
        try {
            const response = await api.put(`/api/admin/employees/${employeeId}`, { shift_id: shiftId });
            return response.data.employee;
        } catch (error) {
            console.error('Error updating employee shift:', error);
            throw error;
        }
    }

    /**
     * Update own profile shift
     */
    async updateOwnShift(shiftId) {
        try {
            const response = await api.put('/api/employees/profile', { shift_id: shiftId });
            return response.data.employee;
        } catch (error) {
            console.error('Error updating own shift:', error);
            throw error;
        }
    }

    /**
     * Fallback: Get employees from localStorage
     */
    getLocalStorageEmployees() {
        try {
            const stored = localStorage.getItem('hrportal_employees');
            if (stored) {
                const parsed = JSON.parse(stored);
                console.log('📦 Found stored employees:', parsed.length);
                return parsed;
            }

            console.log('🔧 No stored employees found, creating test data...');
            // If no employees exist, create default test employees
            const testEmployees = [
                {
                    id: 'EMP001',
                    employee_id: 'EMP001',
                    employee_code: 'EMP001',
                    first_name: 'John',
                    last_name: 'Doe',
                    email: 'john.doe@company.com',
                    department: 'Engineering',
                    designation: 'Senior Developer',
                    employment_status: 'Active'
                },
                {
                    id: 'EMP002',
                    employee_id: 'EMP002',
                    employee_code: 'EMP002',
                    first_name: 'Jane',
                    last_name: 'Smith',
                    email: 'jane.smith@company.com',
                    department: 'Human Resources',
                    designation: 'HR Manager',
                    employment_status: 'Active'
                },
                {
                    id: 'EMP003',
                    employee_id: 'EMP003',
                    employee_code: 'EMP003',
                    first_name: 'Bob',
                    last_name: 'Wilson',
                    email: 'bob.wilson@company.com',
                    department: 'Sales',
                    designation: 'Sales Executive',
                    employment_status: 'Active'
                }
            ];

            // Save for future use
            localStorage.setItem('hrportal_employees', JSON.stringify(testEmployees));
            console.log('✅ Created and saved test employees:', testEmployees.length);
            return testEmployees;
        } catch (error) {
            console.error('Error loading from localStorage:', error);
            return [];
        }
    }

    /**
     * Save employees to localStorage (for backward compatibility)
     */
    saveToLocalStorage(employees) {
        try {
            localStorage.setItem('hrportal_employees', JSON.stringify(employees));
        } catch (error) {
            console.error('Error saving to localStorage:', error);
        }
    }

    /**
     * Toggle login access for an employee (Admin only)
     * @param {string} employeeId - Employee ID
     * @param {boolean} loginDisabled - true to disable, false to enable
     */
    async toggleLoginAccess(employeeId, loginDisabled) {
        try {
            const response = await api.put(`/api/employees/${employeeId}/toggle-login`, {
                login_disabled: loginDisabled
            });
            return response.data;
        } catch (error) {
            console.error('Error toggling login access:', error);
            throw error;
        }
    }

    /**
     * Admin reset password for an employee
     * @param {string} employeeId - Employee ID
     * @param {string} newPassword - New password to set
     */
    async adminResetPassword(employeeId, newPassword) {
        try {
            const response = await api.put(`/api/employees/${employeeId}/reset-password`, {
                new_password: newPassword
            });
            return response.data;
        } catch (error) {
            console.error('Error resetting password:', error);
            throw error;
        }
    }
}

export default new EmployeeService();
